// Bandeau de confirmation d'un RESET de compte prop firm.
//
// Le pipeline de sync ne fait que SUGGÉRER : confirmer réécrit `resetAt` et
// `initialBalance` du compte, donc un faux positif rebaserait silencieusement
// une balance. D'où un composant dédié qui expose les chiffres AVANT de laisser
// l'utilisateur trancher, plutôt qu'un simple bouton "accepter".
//
// Composant isolé (et pas inliné dans AccountDetailPage, déjà à 3000+ lignes)
// pour rester montable ailleurs sans dupliquer la logique de confirmation.

import React, { useState } from "react";
import {
  getAccountResetSuggestions,
  confirmAccountReset,
  dismissAccountReset,
  useQuery,
} from "wasp/client/operations";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "../client/components/ui/button";
import useTranslation from "../client/hooks/useTranslation";
import { CURRENCY } from "../shared/currency";
// Conversions de date extraites et testées à part : une erreur d'un jour ici
// archiverait la mauvaise tranche de trades (borne `>=` sur la date du trade).
import { toDateInputValue, fromDateInputValue } from "./resetDateInput";

function fmtMoney(value: number, currency = CURRENCY) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}


type ResetSuggestion = {
  id: string;
  accountId: string;
  accountName: string | null;
  suggestedResetAt: string | Date;
  expectedBalance: number;
  observedBalance: number;
  discrepancy: number;
  confidence: "high" | "low";
  reason: string;
};

function SuggestionRow({
  suggestion,
  onDone,
}: {
  suggestion: ResetSuggestion;
  onDone: () => void;
}) {
  const t = useTranslation();
  // La date est un état local pré-rempli mais LIBREMENT modifiable : le cash log
  // du broker ne remonte qu'à ~2 jours, donc `suggestedResetAt` est une borne
  // supérieure souvent fausse que l'utilisateur doit pouvoir corriger.
  const [resetAt, setResetAt] = useState(() => toDateInputValue(suggestion.suggestedResetAt));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedResetAt = fromDateInputValue(resetAt);
  const isDateValid = parsedResetAt != null;

  const handleConfirm = async () => {
    // Garde-fou : sans date valide l'action serveur rebaserait sur une date
    // invalide (Zod coerce -> Invalid Date), on bloque en amont.
    if (!isDateValid) {
      setError(t.tradingAccounts.accountResetInvalidDate);
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await confirmAccountReset({ suggestionId: suggestion.id, resetAt: parsedResetAt! });
      onDone();
    } catch (err: any) {
      // Erreur remontée à l'utilisateur : une confirmation silencieusement
      // ratée laisserait croire que la balance a été recalée.
      setError(err?.message ?? t.tradingAccounts.accountResetError);
      setIsSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await dismissAccountReset({ suggestionId: suggestion.id });
      onDone();
    } catch (err: any) {
      setError(err?.message ?? t.tradingAccounts.accountResetError);
      setIsSubmitting(false);
    }
  };

  return (
    // Teinte ambre volontairement distincte du bandeau payout (primary) : le
    // reset est une action lourde et irréversible côté balance.
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-3">
      <div className="flex items-start gap-2">
        <RotateCcw className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
        <div className="text-sm">
          <span className="font-medium">{t.tradingAccounts.accountResetDetectedTitle}</span>
          {suggestion.accountName && (
            <span className="text-muted-foreground"> ({suggestion.accountName})</span>
          )}
          <p className="text-xs text-muted-foreground mt-0.5">
            {t.tradingAccounts.accountResetDetectedDescription}
          </p>
        </div>
      </div>

      {/* Les 3 chiffres qui justifient la suggestion, avant toute décision. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <p className="text-xs text-muted-foreground">{t.tradingAccounts.accountResetObservedBalance}</p>
          <p className="font-mono text-sm font-medium">{fmtMoney(suggestion.observedBalance)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t.tradingAccounts.accountResetExpectedBalance}</p>
          <p className="font-mono text-sm font-medium">{fmtMoney(suggestion.expectedBalance)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t.tradingAccounts.accountResetDiscrepancy}</p>
          <p
            className={`font-mono text-sm font-medium ${
              suggestion.discrepancy < 0 ? "text-destructive" : "text-pnlPositive"
            }`}
          >
            {fmtMoney(suggestion.discrepancy)}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs text-muted-foreground mb-1 block">
            {t.tradingAccounts.accountResetDateLabel}
          </label>
          <input
            type="date"
            value={resetAt}
            disabled={isSubmitting}
            onChange={(e) => {
              setResetAt(e.target.value);
              setError(null);
            }}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          />
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="default" disabled={isSubmitting || !isDateValid} onClick={handleConfirm}>
            {isSubmitting ? t.tradingAccounts.accountResetConfirming : t.tradingAccounts.accountResetConfirm}
          </Button>
          <Button size="sm" variant="ghost" disabled={isSubmitting} onClick={handleDismiss}>
            {t.tradingAccounts.accountResetDismiss}
          </Button>
        </div>
      </div>

      {/* Confiance : "low" = date déduite, l'utilisateur DOIT la vérifier. */}
      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        {suggestion.confidence === "low" && (
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px text-amber-500" />
        )}
        <span>
          {suggestion.confidence === "low"
            ? t.tradingAccounts.accountResetConfidenceLow
            : t.tradingAccounts.accountResetConfidenceHigh}
        </span>
      </p>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function AccountResetSuggestionBanner({ accountId }: { accountId: string }) {
  const { data: suggestions = [], refetch } = useQuery(getAccountResetSuggestions, { accountId });

  const pending = suggestions as unknown as ResetSuggestion[];
  if (!pending.length) return null;

  return (
    <div className="space-y-2">
      {pending.map((s) => (
        <SuggestionRow key={s.id} suggestion={s} onDone={() => refetch()} />
      ))}
    </div>
  );
}
